import React, { useState } from "react";
import { Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import type { AuthUser, LegalConsentsPayload } from "@mvk/shared";
import { apiRequest } from "../api/client";
import {
  EMPTY_LEGAL_CONSENTS,
  LegalConsentCheckboxes,
  legalConsentsReady,
  toLegalConsentsPayload,
  type LegalConsentState,
} from "./LegalConsentCheckboxes";

type Audience = "visitor" | "admin";

type LegalConsentGateProps = {
  audience: Audience;
  onComplete: (user: AuthUser) => void;
};

export function LegalConsentGate({ audience, onComplete }: LegalConsentGateProps) {
  const { t } = useTranslation();
  const [consents, setConsents] = useState<LegalConsentState>(EMPTY_LEGAL_CONSENTS);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async () => {
    setError("");
    if (!legalConsentsReady(consents)) {
      setError(t("login.err_consents"));
      return;
    }
    setLoading(true);
    try {
      const body: LegalConsentsPayload = toLegalConsentsPayload(consents);
      const res = await apiRequest<{ user: AuthUser }>(`/auth/me/consents`, {
        method: "POST",
        body,
        audience,
      });
      onComplete(res.user);
    } catch {
      setError(t("legal.gate_save_error"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[80] bg-black/50 flex items-end sm:items-center justify-center p-4">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-xl p-5 space-y-4">
        <div>
          <h2 className="text-[17px] text-gray-900 font-medium">{t("legal.gate_title")}</h2>
          <p className="text-[13px] text-gray-500 mt-1">{t("legal.gate_subtitle")}</p>
        </div>
        <LegalConsentCheckboxes value={consents} onChange={setConsents} />
        {error ? <p className="text-[12px] text-red-600">{error}</p> : null}
        <button
          type="button"
          disabled={loading || !legalConsentsReady(consents)}
          onClick={() => void submit()}
          className="w-full py-2.5 rounded-xl bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white text-[14px] flex items-center justify-center gap-2"
        >
          {loading ? <Loader2 size={16} className="animate-spin" /> : null}
          {t("legal.gate_continue")}
        </button>
      </div>
    </div>
  );
}
